import { useQuery } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { googleAdsAccountUsersQueryOptions } from '@/lib/query-options';
import { GoogleAdsAccountUser } from '@/lib/types';
import { CheckCircle, XCircle, User, Calendar } from 'lucide-react';
import googleAds from '/google-ads.svg';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom } from '@/store';
import { ApiError } from '@/lib/api-error';
import { NoGoogleAdsAccountSelected } from '@/components/glitch-ui/no-google-ads-account';

export function GoogleAdsPermissions() {
  const { data: users, isLoading, error } = useQuery(googleAdsAccountUsersQueryOptions());
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);

  const getAccessRoleDisplay = (role: string) => {
    const roleLabels = {
      ADMIN: 'Admin',
      STANDARD: 'Standard',
      READ_ONLY: 'Read Only',
      EMAIL_ONLY: 'Email Only',
    };
    return roleLabels[role as keyof typeof roleLabels] || role;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className='w-full rounded-md border-none'>
        <div className='p-4 bg-white rounded-xl'>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-900">Loading Google Ads permissions...</div>
          </div>
        </div>
      </div>
    );
  }

  // No account selected for this organization (#6827): ask for one instead of showing a fetch error.
  if (error instanceof ApiError && error.status === 404) {
    return (
      <NoGoogleAdsAccountSelected description="A Google Ads account needs to be selected for this organization before its Google Ads users can be shown." />
    );
  }

  if (error) {
    return (
      <div className='w-full rounded-md border-none'>
        <div className='p-4 bg-white rounded-xl'>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-900">
              {error instanceof Error ? error.message : 'Failed to load Google Ads permissions'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className='w-full rounded-md border-none'>
        <div className='p-4 bg-white rounded-xl'>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
              <div className="text-gray-500">No users found with Google Ads access</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full rounded-md border-none'>
      <div className='p-4 bg-white rounded-xl'>
        {/* Header section */}
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center p-2">
              <img src={googleAds} alt="Google Ads" className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {googleAdsAccount?.name || googleAdsAccount?.googleads_account_id || 'Google Ads Account'}
              </h3>
              <p className="text-sm text-gray-700">
                Account ID: {googleAdsAccount?.googleads_account_id || 'N/A'}
              </p>
            </div>
          </div>
        </div>
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Access Role</TableHead>
              <TableHead>Campaign Creation</TableHead>
              <TableHead>Access Granted</TableHead>
              <TableHead>Invited By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user: GoogleAdsAccountUser) => (
              <TableRow key={user.user_id}>
                <TableCell>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center p-1">
                      <img src={googleAds} alt="Google Ads" className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-medium">{user.email_address}</div>
                      <div className="text-sm text-gray-900">ID: {user.user_id}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {getAccessRoleDisplay(user.access_role)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {user.can_create_campaigns ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-green-600">Yes</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="text-red-600">No</span>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{formatDate(user.access_creation_date)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {user.inviter_email || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}